import React, { createContext, useContext, useReducer, useCallback, useEffect } from 'react';
import { User } from '@garbera/shared';
import axios from 'axios';
import {
  ProfileState,
  ProfileContextType,
  ProfileUpdateData,
  PasswordChangeData,
  UserSettings,
  AvatarUploadResult,
} from '../types/profile';
import { useAuth } from './useAuth';

// フィールド名を日本語表示名に変換するヘルパー関数
const getFieldDisplayName = (field: string): string => {
  const fieldNames: Record<string, string> = {
    email: 'メールアドレス',
    fullName: '氏名',
    bio: '自己紹介',
    phone: '電話番号',
    organization: '所属組織',
    avatar: 'プロフィール写真',
    birthDate: '生年月日',
    university: '大学名',
    faculty: '学部',
    graduationYear: '卒業年度',
    graduationStatus: '卒業状況',
    currentPrefecture: '現在の都道府県',
    desiredPrefecture: '希望勤務地',
    qualifications: '資格',
    desiredJobType: '希望職種',
    mediationCount: '仲介実績件数',
    monthlyMaxSales: '月間最高売上',
    totalSales: '累計売上',
    realEstateExperienceMonths: '不動産業界経験月数',
    entrepreneurialInterest: '起業への関心',
  };
  
  return fieldNames[field] || field;
};

// Action types
type ProfileAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_UPDATING'; payload: boolean }
  | { type: 'SET_UPLOADING_AVATAR'; payload: boolean }
  | { type: 'SET_USER'; payload: User }
  | { type: 'SET_SETTINGS'; payload: UserSettings }
  | { type: 'SET_ERROR'; payload: string }
  | { type: 'SET_AVATAR_ERROR'; payload: string }
  | { type: 'SET_UPLOAD_PROGRESS'; payload: { loaded: number; total: number; percentage: number } | null }
  | { type: 'SET_SUCCESS_MESSAGE'; payload: string }
  | { type: 'CLEAR_ERROR' }
  | { type: 'CLEAR_AVATAR_ERROR' }
  | { type: 'CLEAR_SUCCESS_MESSAGE' };

// Initial state
const initialState: ProfileState = {
  user: null,
  settings: null,
  isLoading: false,
  isUpdating: false,
  isUploadingAvatar: false,
  error: null,
  avatarError: null,
  uploadProgress: null,
  successMessage: null,
};

// Reducer
const profileReducer = (state: ProfileState, action: ProfileAction): ProfileState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_UPDATING':
      return { ...state, isUpdating: action.payload };
    case 'SET_UPLOADING_AVATAR':
      return { ...state, isUploadingAvatar: action.payload };
    case 'SET_USER':
      return { ...state, user: action.payload };
    case 'SET_SETTINGS':
      return { ...state, settings: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'SET_AVATAR_ERROR':
      return { ...state, avatarError: action.payload };
    case 'SET_UPLOAD_PROGRESS':
      return { ...state, uploadProgress: action.payload };
    case 'SET_SUCCESS_MESSAGE':
      return { ...state, successMessage: action.payload };
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    case 'CLEAR_AVATAR_ERROR':
      return { ...state, avatarError: null };
    case 'CLEAR_SUCCESS_MESSAGE':
      return { ...state, successMessage: null };
    default:
      return state;
  }
};

// Context
const ProfileContext = createContext<ProfileContextType | undefined>(undefined);

// Provider component
interface ProfileProviderProps {
  children: React.ReactNode;
}

export const ProfileProvider: React.FC<ProfileProviderProps> = ({ children }) => {
  const { user: authUser } = useAuth();
  const [state, dispatch] = useReducer(profileReducer, initialState);

  // Use authUser as fallback when profile user is null
  const effectiveUser = state.user || authUser;

  // Load user profile
  const refreshProfile = useCallback(async (): Promise<void> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'CLEAR_ERROR' });

      const response = await axios.get('/api/users/profile', {
        timeout: 30000 // 30秒タイムアウト
      });
      
      dispatch({ type: 'SET_USER', payload: response.data.data.user });
    } catch (error) {
      
      let errorMessage = 'プロフィールの読み込みに失敗しました';
      
      if (axios.isAxiosError(error)) {
        if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
          errorMessage = 'プロフィールの読み込みがタイムアウトしました。しばらく時間をおいてから再度お試しください。';
        } else if (error.response?.status >= 500) {
          errorMessage = 'サーバーエラーが発生しました。しばらく時間をおいてから再度お試しください。';
        }
      }
      
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, []);

  // Update profile
  const updateProfile = useCallback(async (data: ProfileUpdateData): Promise<void> => {
    try {
      dispatch({ type: 'SET_UPDATING', payload: true });
      dispatch({ type: 'CLEAR_ERROR' });

      // データの正規化：数値フィールド、真偽値フィールド、都道府県コードを適切に処理
      const normalizedData: any = { ...data };
      
      // 数値フィールドを適切に変換
      const numericFields = [
        'mediationCount',
        'monthlyMaxSales',
        'totalSales',
        'realEstateExperienceMonths',
        'graduationYear',
      ];
      
      numericFields.forEach((field) => {
        if (field in normalizedData) {
          const value = normalizedData[field];
          // 空文字列、null、undefined の場合は undefined に統一
          if (value === '' || value === null || value === undefined) {
            normalizedData[field] = undefined;
          } else if (typeof value === 'string') {
            // 文字列の場合は数値に変換
            const numValue = Number(value);
            normalizedData[field] = isNaN(numValue) ? undefined : numValue;
          }
        }
      });
      
      // 真偽値フィールドの処理
      if ('entrepreneurialInterest' in normalizedData) {
        const value = normalizedData.entrepreneurialInterest;
        if (value === '' || value === null || value === undefined) {
          normalizedData.entrepreneurialInterest = undefined;
        } else if (typeof value === 'string') {
          normalizedData.entrepreneurialInterest = value === 'true' || value === '1';
        }
      }
      
      // 都道府県フィールドの処理（空文字をundefinedに）
      if ('currentPrefecture' in normalizedData) {
        const value = normalizedData.currentPrefecture;
        if (value === '' || value === null) {
          normalizedData.currentPrefecture = undefined;
        }
      }
      
      if ('desiredPrefecture' in normalizedData) {
        const value = normalizedData.desiredPrefecture;
        if (value === '' || value === null) {
          normalizedData.desiredPrefecture = undefined;
        }
      }
      
      // 卒業状況の処理（文字列型を確保）
      if ('graduationStatus' in normalizedData) {
        const value = normalizedData.graduationStatus;
        if (value === '' || value === null || value === undefined) {
          normalizedData.graduationStatus = undefined;
        } else if (typeof value !== 'string') {
          // オブジェクトの場合はvalueプロパティを取得
          normalizedData.graduationStatus = String(value);
        }
      }

      const response = await axios.put('/api/users/profile', normalizedData);
      dispatch({ type: 'SET_USER', payload: response.data.data.user });
      dispatch({ type: 'SET_SUCCESS_MESSAGE', payload: 'プロフィールを保存しました' });
    } catch (error: any) {
      // エラーメッセージ：主に形式が間違っている場合（バリデーションエラー）を表示
      let errorMessage = '';
      let shouldDisplayError = false;
      
      if (axios.isAxiosError(error) && error.response) {
        const errorCode = error.response.data?.error?.code;
        const backendMessage = error.response.data?.error?.message || error.response.data?.message;
        
        // バリデーションエラー（形式の間違い）の場合は必ず表示
        if (errorCode === 'VALIDATION_ERROR') {
          shouldDisplayError = true;
          const details = error.response.data?.error?.details;
          if (details && Array.isArray(details)) {
            const fieldErrors = details.map((detail: any) => {
              const field = detail.field || detail.property;
              const message = detail.message || detail.constraints?.[Object.keys(detail.constraints)[0]];
              return `${getFieldDisplayName(field)}: ${message}`;
            }).join('\n');
            errorMessage = `入力形式に誤りがあります:\n${fieldErrors}`;
          } else if (backendMessage) {
            errorMessage = backendMessage;
          } else {
            errorMessage = '入力内容を確認してください。';
          }
        } else if (errorCode === 'EMAIL_ALREADY_EXISTS') {
          shouldDisplayError = true;
          errorMessage = 'このメールアドレスは既に使用されています。';
        } else if (errorCode === 'INVALID_FORMAT') {
          shouldDisplayError = true;
          errorMessage = '入力形式が正しくありません。各項目の入力例を参考にしてください。';
        } else if (errorCode === 'REQUIRED_FIELD') {
          shouldDisplayError = true;
          errorMessage = '必須項目が入力されていません。';
        } else if (error.response.status === 401) {
          shouldDisplayError = true;
          errorMessage = '認証エラーが発生しました。再度ログインしてください。';
        } else if (error.response.status === 403) {
          shouldDisplayError = true;
          errorMessage = 'この操作を実行する権限がありません。';
        } else if (error.response.status >= 500) {
          shouldDisplayError = true;
          errorMessage = 'サーバーエラーが発生しました。しばらく時間をおいてから再度お試しください。';
        }
        // その他のエラーは表示しない（ログには残る）
      } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        shouldDisplayError = true;
        errorMessage = '通信がタイムアウトしました。インターネット接続を確認してから再度お試しください。';
      } else if (error.code === 'ERR_NETWORK') {
        shouldDisplayError = true;
        errorMessage = 'ネットワークエラーが発生しました。インターネット接続を確認してください。';
      }
      
      // エラーメッセージを表示する必要がある場合のみ設定
      if (shouldDisplayError && errorMessage) {
        dispatch({ type: 'SET_ERROR', payload: errorMessage });
      }
      
      throw error;
    } finally {
      dispatch({ type: 'SET_UPDATING', payload: false });
    }
  }, []);

  // Update settings
  const updateSettings = useCallback(async (settings: Partial<UserSettings>): Promise<void> => {
    try {
      dispatch({ type: 'SET_UPDATING', payload: true });
      dispatch({ type: 'CLEAR_ERROR' });

      const response = await axios.put('/api/users/settings', settings);
      dispatch({ type: 'SET_SETTINGS', payload: response.data.data });
      dispatch({ type: 'SET_SUCCESS_MESSAGE', payload: '設定を保存しました' });
    } catch (error: any) {
      let errorMessage = '設定の更新に失敗しました';
      
      if (axios.isAxiosError(error) && error.response) {
        const errorCode = error.response.data?.error?.code;
        const backendMessage = error.response.data?.error?.message || error.response.data?.message;
        
        switch (errorCode) {
          case 'VALIDATION_ERROR':
            errorMessage = '設定内容が正しくありません。入力内容を確認してください。';
            break;
          default:
            if (backendMessage) {
              errorMessage = backendMessage;
            }
        }
        
        if (error.response.status >= 500) {
          errorMessage = 'サーバーエラーが発生しました。しばらく時間をおいてから再度お試しください。';
        }
      }
      
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      throw error;
    } finally {
      dispatch({ type: 'SET_UPDATING', payload: false });
    }
  }, []);

  // Change password
  const changePassword = useCallback(async (data: PasswordChangeData): Promise<void> => {
    try {
      dispatch({ type: 'SET_UPDATING', payload: true });
      dispatch({ type: 'CLEAR_ERROR' });

      // フロントエンドでの事前バリデーション
      if (!data.currentPassword || !data.newPassword) {
        const errorMessage = '現在のパスワードと新しいパスワードを入力してください。';
        dispatch({ type: 'SET_ERROR', payload: errorMessage });
        throw new Error('MISSING_PASSWORD_FIELDS');
      }

      if (data.newPassword.length < 8) {
        const errorMessage = '新しいパスワードは8文字以上で入力してください。';
        dispatch({ type: 'SET_ERROR', payload: errorMessage });
        throw new Error('PASSWORD_TOO_SHORT');
      }

      if (data.currentPassword === data.newPassword) {
        const errorMessage = '現在のパスワードと同じパスワードは設定できません。';
        dispatch({ type: 'SET_ERROR', payload: errorMessage });
        throw new Error('PASSWORD_SAME_AS_CURRENT');
      }

      // バックエンドが期待する形式に変換（confirmPasswordを除去）
      const requestData = {
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      };

      await axios.post('/api/users/change-password', requestData);
      dispatch({ type: 'SET_SUCCESS_MESSAGE', payload: 'パスワードを変更しました' });
    } catch (error: any) {
      // 既に設定したエラーの場合はそのまま投げる
      if (['MISSING_PASSWORD_FIELDS', 'PASSWORD_TOO_SHORT', 'PASSWORD_SAME_AS_CURRENT'].includes(error.message)) {
        throw error;
      }
      
      let errorMessage = 'パスワードの変更に失敗しました';
      
      if (axios.isAxiosError(error) && error.response) {
        const errorCode = error.response.data?.error?.code;
        const backendMessage = error.response.data?.error?.message || error.response.data?.message;
        
        switch (errorCode) {
          case 'INVALID_CURRENT_PASSWORD':
            errorMessage = '現在のパスワードが正しくありません。';
            break;
          case 'PASSWORD_TOO_WEAK':
            errorMessage = '新しいパスワードは、英字、数字、記号を含む8文字以上で設定してください。';
            break;
          case 'PASSWORD_RECENTLY_USED':
            errorMessage = '最近使用したパスワードは設定できません。別のパスワードを入力してください。';
            break;
          case 'VALIDATION_ERROR':
            errorMessage = 'パスワードの形式が正しくありません。';
            break;
          default:
            if (backendMessage) {
              errorMessage = backendMessage;
            }
        }
        
        if (error.response.status >= 500) {
          errorMessage = 'サーバーエラーが発生しました。しばらく時間をおいてから再度お試しください。';
        }
      }
      
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      throw error;
    } finally {
      dispatch({ type: 'SET_UPDATING', payload: false });
    }
  }, []);

  // Upload avatar
  const uploadAvatar = useCallback(async (file: File): Promise<AvatarUploadResult> => {
    try {
      dispatch({ type: 'SET_UPLOADING_AVATAR', payload: true });
      dispatch({ type: 'CLEAR_AVATAR_ERROR' });

      // フロントエンドでの事前バリデーション
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        const errorMessage = 'ファイルサイズが大きすぎます。5MB以下の画像を選択してください。';
        dispatch({ type: 'SET_AVATAR_ERROR', payload: errorMessage });
        throw new Error('FILE_TOO_LARGE');
      }

      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        const errorMessage = '対応していないファイル形式です。JPEG、PNG、GIF、WebP形式の画像を選択してください。';
        dispatch({ type: 'SET_AVATAR_ERROR', payload: errorMessage });
        throw new Error('INVALID_FILE_TYPE');
      }

      const formData = new FormData();
      formData.append('avatar', file);

      const response = await axios.post('/api/users/avatar', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percentage = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            dispatch({
              type: 'SET_UPLOAD_PROGRESS',
              payload: {
                loaded: progressEvent.loaded,
                total: progressEvent.total,
                percentage,
              },
            });
          }
        },
      });

      // Update user with new avatar URL
      dispatch({ type: 'SET_USER', payload: response.data.data.user });

      return {
        url: response.data.data.url,
        fileSize: response.data.data.fileSize,
        fileName: response.data.data.fileName,
      };
    } catch (error: any) {
      // 既に設定したエラーの場合はそのまま投げる
      if (['FILE_TOO_LARGE', 'INVALID_FILE_TYPE'].includes(error.message)) {
        throw error;
      }
      
      let errorMessage = 'アバターのアップロードに失敗しました';
      
      if (axios.isAxiosError(error) && error.response) {
        const errorCode = error.response.data?.error?.code;
        const backendMessage = error.response.data?.error?.message || error.response.data?.message;
        
        switch (errorCode) {
          case 'NO_FILE_PROVIDED':
            errorMessage = 'ファイルが選択されていません。';
            break;
          case 'FILE_TOO_LARGE':
            errorMessage = 'ファイルサイズが大きすぎます。5MB以下の画像を選択してください。';
            break;
          case 'INVALID_FILE_TYPE':
            errorMessage = '対応していないファイル形式です。画像ファイルを選択してください。';
            break;
          case 'UPLOAD_FAILED':
            errorMessage = 'アップロードに失敗しました。ネットワーク接続を確認してから再度お試しください。';
            break;
          default:
            if (backendMessage) {
              errorMessage = backendMessage;
            }
        }
        
        if (error.response.status >= 500) {
          errorMessage = 'サーバーエラーが発生しました。しばらく時間をおいてから再度お試しください。';
        }
      } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        errorMessage = 'アップロードがタイムアウトしました。ファイルサイズを確認してから再度お試しください。';
      } else if (error.code === 'ERR_NETWORK') {
        errorMessage = 'ネットワークエラーが発生しました。インターネット接続を確認してください。';
      }
      
      dispatch({ type: 'SET_AVATAR_ERROR', payload: errorMessage });
      throw error;
    } finally {
      dispatch({ type: 'SET_UPLOADING_AVATAR', payload: false });
      dispatch({ type: 'SET_UPLOAD_PROGRESS', payload: null });
    }
  }, []);

  // Clear error
  const clearError = useCallback((): void => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, []);

  // Clear avatar error
  const clearAvatarError = useCallback((): void => {
    dispatch({ type: 'CLEAR_AVATAR_ERROR' });
  }, []);

  // Clear success message
  const clearSuccessMessage = useCallback((): void => {
    dispatch({ type: 'CLEAR_SUCCESS_MESSAGE' });
  }, []);

  // Load profile on mount
  useEffect(() => {
    refreshProfile();
  }, [refreshProfile]);

  const value: ProfileContextType = {
    ...state,
    user: effectiveUser, // Use effective user (with fallback to authUser)
    updateProfile,
    updateSettings,
    changePassword,
    uploadAvatar,
    clearError,
    clearAvatarError,
    clearSuccessMessage,
    refreshProfile,
  };

  return (
    <ProfileContext.Provider value={value}>
      {children}
    </ProfileContext.Provider>
  );
};

// Hook
export const useProfile = (): ProfileContextType => {
  const context = useContext(ProfileContext);
  if (context === undefined) {
    throw new Error('useProfile must be used within a ProfileProvider');
  }
  return context;
};